import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { CreditCard, Plus, Minus, Trash2, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PLAN_OPTIONS } from './shared';

export const AdminBulkDialog = ({
  open, onClose, selectedCount, isSuper,
  bulkAction, setBulkAction,
  bulkPlan, setBulkPlan,
  bulkCreditsDelta, setBulkCreditsDelta,
  busy, onRun,
}) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="bg-zinc-950 border-white/10" data-testid="admin-bulk-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading">Bulk action · {selectedCount} users</DialogTitle>
        <DialogDescription>
          Apply a change to every selected user in one go. Audit log will record the operation.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 pt-2">
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => setBulkAction('plan')}
            className={cn(
              'p-3 rounded-lg border text-left transition-colors',
              bulkAction === 'plan'
                ? 'border-primary/40 bg-primary/10'
                : 'border-white/5 bg-zinc-900/40 hover:border-white/10'
            )}
            data-testid="admin-bulk-tab-plan"
          >
            <CreditCard className="w-4 h-4 mb-1.5 text-indigo-300" />
            <p className="text-sm font-medium">Set plan</p>
          </button>
          <button
            type="button"
            onClick={() => setBulkAction('credits')}
            className={cn(
              'p-3 rounded-lg border text-left transition-colors',
              bulkAction === 'credits'
                ? 'border-primary/40 bg-primary/10'
                : 'border-white/5 bg-zinc-900/40 hover:border-white/10'
            )}
            data-testid="admin-bulk-tab-credits"
          >
            {bulkCreditsDelta >= 0
              ? <Plus className="w-4 h-4 mb-1.5 text-emerald-300" />
              : <Minus className="w-4 h-4 mb-1.5 text-amber-300" />}
            <p className="text-sm font-medium">Adjust credits</p>
          </button>
          <button
            type="button"
            onClick={() => setBulkAction('delete')}
            disabled={!isSuper}
            className={cn(
              'p-3 rounded-lg border text-left transition-colors',
              bulkAction === 'delete'
                ? 'border-red-500/40 bg-red-500/10'
                : 'border-white/5 bg-zinc-900/40 hover:border-white/10',
              !isSuper && 'opacity-40 cursor-not-allowed'
            )}
            data-testid="admin-bulk-tab-delete"
          >
            <Trash2 className="w-4 h-4 mb-1.5 text-red-400" />
            <p className="text-sm font-medium">Delete users</p>
          </button>
        </div>

        {bulkAction === 'plan' && (
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">New plan</label>
            <Select value={bulkPlan} onValueChange={setBulkPlan}>
              <SelectTrigger className="mt-1.5 bg-zinc-900/60 border-white/10" data-testid="admin-bulk-plan-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLAN_OPTIONS.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1.5">Monthly credit allowance resets to the plan default.</p>
          </div>
        )}

        {bulkAction === 'credits' && (
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Credit delta (purchased)</label>
            <Input
              type="number"
              value={bulkCreditsDelta}
              onChange={(e) => setBulkCreditsDelta(e.target.value)}
              className="mt-1.5 bg-zinc-900/60 border-white/10"
              data-testid="admin-bulk-credits-input"
            />
            <p className="text-xs text-muted-foreground mt-1.5">
              Positive values add credits, negative subtracts. Doesn't affect monthly allowance.
            </p>
          </div>
        )}

        {bulkAction === 'delete' && (
          <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20">
            <p className="text-sm text-red-300">
              This permanently removes {selectedCount} user account{selectedCount === 1 ? '' : 's'}.
              Your own account and the last super admin are automatically excluded.
            </p>
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button
          onClick={onRun}
          disabled={busy || (bulkAction === 'delete' && !isSuper)}
          variant={bulkAction === 'delete' ? 'destructive' : 'default'}
          data-testid="admin-bulk-run-button"
        >
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          {bulkAction === 'plan' && 'Apply plan'}
          {bulkAction === 'credits' && 'Apply credit change'}
          {bulkAction === 'delete' && 'Delete users'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
